import { useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  LevelBadge,
  Loading,
  Pill,
  Segmented,
} from '../components/ui';
import { InvitePerson } from '../components/InvitePerson';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { shortDate } from '../lib/format';
import type { ManagedUser, TreeNode } from '../lib/types';
import { useApi } from '../lib/useApi';
import './shared.css';

type Filter = 'all' | 'active' | 'invited' | 'suspended';

const ROLE_LABEL: Record<string, string> = {
  l1_admin: 'Administrator',
  l1_underwriter: 'Underwriter',
  l2_office: 'Office principal',
  l3_manager: 'Agent manager',
  l4_agent: 'Agent',
  merchant_support: 'Merchant support',
};

/**
 * Administering the people in your downline.
 *
 * Everything here is an account action rather than a hierarchy one: suspend,
 * unlock, reset a lost second factor, sign someone out. Moving people between
 * offices stays on the organisation screen, where the tree is.
 */
export function People() {
  const { user, can } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const { data, error: loadError, loading, reload } = useApi<ManagedUser[]>('/users');
  // The dialog needs the tree to offer the offices this person may invite into.
  const tree = useApi<TreeNode>(can('org.invite') ? '/org/tree' : null);

  // Only while there is nothing to show. Blanking the screen on every refresh
  // unmounts whatever is open on top of it — which is how an invitation's
  // confirmation used to disappear the moment it succeeded.
  if (loading && !data) return <Loading label="Loading your people…" />;
  if (loadError) return <ErrorState error={loadError} onRetry={reload} />;

  const people = (data ?? [])
    .filter((p) => (filter === 'all' ? true : p.status === filter))
    .filter((p) =>
      search
        ? p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.email.toLowerCase().includes(search.toLowerCase())
        : true,
    );

  const counts = {
    all: data?.length ?? 0,
    active: (data ?? []).filter((p) => p.status === 'active').length,
    invited: (data ?? []).filter((p) => p.status === 'invited').length,
    suspended: (data ?? []).filter((p) => p.status === 'suspended').length,
  };

  async function remove(person: ManagedUser) {
    setBusy(person.id);
    setError(null);
    setNotice(null);
    try {
      await api.del(`/users/${person.id}`);
      setNotice(
        `${person.name || person.email} was deleted. It is in the recycle bin if that was a mistake.`,
      );
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  async function act(
    person: ManagedUser,
    path: string,
    body: unknown,
    done: string,
  ) {
    setBusy(person.id);
    setError(null);
    setNotice(null);
    setInviteLink(null);
    try {
      const res = await api.post<{ inviteUrl?: string; emailed?: boolean }>(
        `/users/${person.id}${path}`,
        body,
      );
      setNotice(done);
      // With no mail relay configured the API hands the link back instead of
      // sending it, so an office can still get someone started.
      if (res?.inviteUrl) setInviteLink(res.inviteUrl);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Administration', 'People']}
        title="People"
        actions={
          <>
            <Pill tone="neutral">{counts.all} in your downline</Pill>
            {can('org.invite') && (
              <Button
                variant="primary"
                icon={Icon.plus({ size: 14 })}
                onClick={() => setInviting(true)}
              >
                Invite a person
              </Button>
            )}
          </>
        }
      />

      <ScopeBar>
        Everyone at or beneath <strong>{user?.nodeName}</strong>. You cannot
        administer anyone whose role sits above your own.
      </ScopeBar>

      <PageBody>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="notice notice--ok" role="status">
            {Icon.check({ size: 15 })}
            <span>{notice}</span>
          </div>
        )}
        {inviteLink && (
          <div className="notice notice--info">
            {Icon.alert({ size: 15 })}
            <span>
              No mail relay is configured, so nothing was sent. Hand this link
              over yourself — it is valid once:{' '}
              <code className="mono" style={{ wordBreak: 'break-all' }}>
                {inviteLink}
              </code>
            </span>
          </div>
        )}

        <Card>
          <CardHeader
            title="Accounts"
            count={people.length}
            action={
              <input
                className="input"
                placeholder="Search name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 220 }}
              />
            }
          />

          <div style={{ marginBottom: 14 }}>
            <Segmented<Filter>
              label="Filter by status"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All', count: counts.all },
                { value: 'active', label: 'Active', count: counts.active },
                { value: 'invited', label: 'Invited', count: counts.invited },
                { value: 'suspended', label: 'Suspended', count: counts.suspended },
              ]}
            />
          </div>

          {people.length === 0 ? (
            <Empty
              title="Nobody here"
              detail="Invite someone and they will appear here as soon as the link is sent."
            />
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              {people.map((person) => (
                <div key={person.id} className="person">
                  <Avatar initials={person.initials} />
                  <div className="person__body">
                    <div className="person__head">
                      <strong>{person.name || person.email}</strong>
                      <LevelBadge level={person.level} />
                      <span className="person__role">
                        {ROLE_LABEL[person.role] ?? person.role}
                      </span>
                      {person.status === 'suspended' && <Pill tone="danger">Suspended</Pill>}
                      {person.status === 'invited' && <Pill tone="warn">Invited</Pill>}
                      {person.lockedUntil &&
                        new Date(person.lockedUntil) > new Date() && (
                          <Pill tone="warn">Locked out</Pill>
                        )}
                    </div>
                    <div className="person__meta">
                      {person.email}
                      {' · '}
                      {person.mfaEnabled ? (
                        <>
                          two-factor: {person.mfaMethods.join(', ')} ·{' '}
                          {person.recoveryCodesRemaining} recovery codes
                        </>
                      ) : (
                        <span className="person__warn">no second factor yet</span>
                      )}
                      {person.lastLoginAt
                        ? ` · last in ${shortDate(person.lastLoginAt)}`
                        : ' · never signed in'}
                      {person.suspensionReason ? ` · ${person.suspensionReason}` : ''}
                    </div>

                    <div className="row" style={{ gap: 7, marginTop: 9 }}>
                      {person.status === 'suspended' ? (
                        <Button
                          disabled={busy === person.id}
                          onClick={() =>
                            void act(person, '/reactivate', {}, `${person.name} can sign in again.`)
                          }
                        >
                          Restore access
                        </Button>
                      ) : (
                        <Button
                          disabled={busy === person.id || person.id === user?.userId}
                          onClick={() =>
                            void act(
                              person,
                              '/suspend',
                              { reason: 'Suspended from the people screen' },
                              `${person.name || person.email} is suspended and signed out.`,
                            )
                          }
                        >
                          Suspend
                        </Button>
                      )}

                      {person.lockedUntil && new Date(person.lockedUntil) > new Date() && (
                        <Button
                          disabled={busy === person.id}
                          onClick={() =>
                            void act(person, '/unlock', {}, 'Lockout cleared.')
                          }
                        >
                          Clear lockout
                        </Button>
                      )}

                      {person.status === 'invited' && (
                        <Button
                          disabled={busy === person.id}
                          onClick={() =>
                            void act(
                              person,
                              '/resend-invitation',
                              {},
                              'A fresh invitation was issued.',
                            )
                          }
                        >
                          Reissue invitation
                        </Button>
                      )}

                      {person.mfaEnabled && (
                        <Button
                          disabled={busy === person.id}
                          onClick={() =>
                            void act(
                              person,
                              '/reset-mfa',
                              {},
                              `Two-factor cleared for ${person.name}. They must set it up again.`,
                            )
                          }
                        >
                          Reset two-factor
                        </Button>
                      )}

                      <Button
                        disabled={busy === person.id}
                        onClick={() =>
                          void act(
                            person,
                            '/revoke-sessions',
                            {},
                            'Signed out of every device.',
                          )
                        }
                      >
                        Sign out everywhere
                      </Button>

                      {/* Suspension is for someone who has left; this is for an
                          account that should not exist. Recoverable either way. */}
                      <Button
                        variant="danger"
                        disabled={busy === person.id || person.id === user?.userId}
                        onClick={() => void remove(person)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </PageBody>

      <InvitePerson
        open={inviting}
        tree={tree.data}
        onClose={() => setInviting(false)}
        onInvited={reload}
      />
    </>
  );
}
